import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useCreateNetworkMutation,
  useDeleteNetworkMutation,
  useGetTrackingMethodsQuery,
  useUpdateNetworkMutation,
  useGetTrackedUrlPreviewMutation,
} from "state/api";
import { Loader2 } from "lucide-react";

// Componentes separados
import NotificationToast from "./components/NotificationToast";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import FormHeader from "./components/FormHeader";
import NetworkFormFields from "./components/NetworkFormFields";
import UrlPreviewPanel from "./components/UrlPreviewPanel";

// Schema de validação
const networkSchema = Yup.object({
  name: Yup.string().required("O nome da rede é obrigatório."),
  tracking_method: Yup.string().required("Método de rastreamento é obrigatório."),
  template: Yup.string().when('tracking_method', {
    is: 'template_builder',
    then: (schema) => schema.required('Template é obrigatório para este método'),
    otherwise: (schema) => schema.optional()
  }),
  params: Yup.object().optional(),
});

const defaultValue = {
  name: "",
  tracking_method: "",
  template: "",
  params: {},
};

const NetworkForm = ({ title, network = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Mutations e queries
  const [createNetwork] = useCreateNetworkMutation();
  const [updateNetwork] = useUpdateNetworkMutation();
  const [deleteNetwork] = useDeleteNetworkMutation();
  const { data: trackingMethods = [], isLoading: isLoadingMethods } = useGetTrackingMethodsQuery();
  const [getTrackedUrlPreview] = useGetTrackedUrlPreviewMutation();
  
  // Estados
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [notification, setNotification] = useState(null);
  const [preview, setPreview] = useState({
    url: "http://example.com/product?id=123",
    result: "",
    isLoading: false
  });

  // Formik setup
  const formik = useFormik({
    initialValues: network?.data || defaultValue,
    validationSchema: networkSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const body = networkSchema.cast(values);
        let resp;
        
        if (id) {
          resp = await updateNetwork({ id, body });
        } else {
          resp = await createNetwork(body);
        }
        
        if (!resp.error) {
          setNotification({
            type: 'success',
            message: id ? 'Rede atualizada com sucesso!' : 'Rede criada com sucesso!'
          });
          
          if (!id) {
            setTimeout(() => navigate("/network"), 1500);
          }
        } else {
          setNotification({
            type: 'error',
            message: resp.error?.data?.[0] || 'Erro ao salvar rede'
          });
        }
      } catch (error) {
        setNotification({
          type: 'error',
          message: 'Erro inesperado ao salvar rede'
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  // Preview da URL com debounce
  useEffect(() => {
    const getPreview = async () => {
      if (!preview.url || !formik.values.tracking_method) return;
      
      setPreview(prev => ({ ...prev, isLoading: true }));
      
      try {
        const { data } = await getTrackedUrlPreview({
          url: preview.url,
          ...formik.values,
        });
        setPreview(prev => ({ 
          ...prev, 
          result: data?.preview || '',
          isLoading: false 
        }));
      } catch (error) {
        setPreview(prev => ({ 
          ...prev, 
          result: 'Erro ao gerar preview',
          isLoading: false 
        }));
      }
    };

    const timeoutId = setTimeout(getPreview, 500);
    return () => clearTimeout(timeoutId);
  }, [formik.values, preview.url, getTrackedUrlPreview]);

  // Handlers
  const handleDelete = useCallback(async () => {
    try {
      await deleteNetwork(id);
      setNotification({
        type: 'success',
        message: 'Rede excluída com sucesso!'
      });
      setTimeout(() => navigate("/network"), 1500);
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Erro ao excluir rede'
      });
    }
    setShowDeleteDialog(false);
  }, [id, deleteNetwork, navigate]);

  const handleClone = useCallback(() => {
    const clonedValues = {
      ...formik.values,
      name: `${formik.values.name} (Cópia)`,
    };
    formik.setValues(clonedValues);
    setNotification({
      type: 'success',
      message: 'Rede clonada! Altere o nome e salve.'
    });
  }, [formik]);

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setNotification({
      type: 'success',
      message: 'Copiado para a área de transferência!'
    });
  }, []);

  const handleUrlChange = useCallback((url) => {
    setPreview(prev => ({ ...prev, url }));
  }, []);

  // Loading state
  if (isLoadingMethods) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Cabeçalho */}
        <FormHeader
          title={title}
          id={id}
          isValid={formik.isValid}
          isSubmitting={isSubmitting}
          network={network}
          onBack={() => navigate("/network")}
          onClone={handleClone}
          onDelete={() => setShowDeleteDialog(true)}
        />

        {/* Formulário */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Coluna esquerda - Formulário */}
          <div className="space-y-6">
            <NetworkFormFields 
              formik={formik} 
              trackingMethods={trackingMethods} 
            />
          </div>

          {/* Coluna direita - Preview */}
          <div className="space-y-6">
            <UrlPreviewPanel
              preview={preview}
              onUrlChange={handleUrlChange}
              onCopy={copyToClipboard}
            />
          </div>
        </div>
      </div>

      {/* Componentes de UI */}
      <NotificationToast 
        notification={notification} 
        onClose={() => setNotification(null)} 
      />
      <DeleteConfirmationModal
        isOpen={showDeleteDialog}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};

export default NetworkForm;